/**
 * userCalendarFilterApi — Symfony API client for the user-scoped saved filter
 * CRUD endpoints (Phase 1, Remediation 9, sub-epic 9.4).
 *
 * The backend treats `filter` as an opaque JSON blob (see
 * `symphony-holdshort/src/Entity/User/UserCalendarFilter.php:32`), so the
 * React layer owns the shape. Per-view routing (`filter.view === 'month'`)
 * is enforced client-side in the slice; this module is pure transport.
 *
 * Endpoints (verified against `UserCalendarFilterController.php:19-180`):
 *   - GET    `/user/filter`       → list the current user's saved filters
 *   - POST   `/user/filter`       → create a new saved filter
 *   - POST   `/user/filter/{id}`  → update an existing saved filter
 *   - DELETE `/user/filter/{id}`  → delete a saved filter
 *
 * @module userCalendarFilterApi
 */

import BaseApi from './BaseApi.js';
import { apiRequest } from './utils.js';

/**
 * GET /api/v1/user/filter — list every saved filter belonging to the current
 * user, across all views. The slice is responsible for per-view filtering
 * (Month/Week/Day) via `filter.view`.
 *
 * @returns {Promise<*>} Raw `BaseApi.get` response.
 */
export function listUserCalendarFilters() {
	return BaseApi.get('/user/filter');
}

/**
 * Send a payload as `application/x-www-form-urlencoded`. The user-filter
 * controller reads values via `$request->get(...)` / `$request->request->get(...)`,
 * both of which ONLY populate for `multipart/form-data` or
 * `application/x-www-form-urlencoded` — a JSON body yields null and the
 * controller's `Assert::notNull` throws a 500.
 *
 * `filter` is nested so we flatten it into PHP's bracket-array syntax
 * (`filter[key]=value`, `filter[arr][]=v1`). The backend then reconstructs
 * the array and passes it to `Assert::isArray($filter, ...)`.
 */
function encodeFormPayload(payload) {
	const params = new URLSearchParams();
	const append = (key, value) => {
		if (value === null || value === undefined) return;
		if (Array.isArray(value)) {
			value.forEach(v => append(`${key}[]`, v));
			return;
		}
		if (typeof value === 'object') {
			Object.entries(value).forEach(([k, v]) => append(`${key}[${k}]`, v));
			return;
		}
		if (typeof value === 'boolean') {
			params.append(key, value ? '1' : '0');
			return;
		}
		params.append(key, String(value));
	};
	Object.entries(payload || {}).forEach(([k, v]) => append(k, v));
	return params.toString();
}

function postForm(endpoint, payload) {
	return apiRequest(endpoint, {
		method: 'POST',
		headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
		body: encodeFormPayload(payload)
	});
}

/**
 * POST /api/v1/user/filter — create a new saved filter.
 *
 * @param {string} name
 * @param {Object} filter Opaque JSON blob. The slice embeds a `view` key.
 * @returns {Promise<*>}
 */
export function createUserCalendarFilter(name, filter) {
	return postForm('/user/filter', { name, filter });
}

/**
 * POST /api/v1/user/filter/{id} — replace an existing saved filter's name
 * and/or blob. The controller treats POST as an upsert per legacy
 * conventions — there is no PUT route.
 *
 * @param {number|string} id
 * @param {string} name
 * @param {Object} filter
 * @returns {Promise<*>}
 */
export function updateUserCalendarFilter(id, name, filter) {
	return postForm(`/user/filter/${id}`, { name, filter });
}

/**
 * DELETE /api/v1/user/filter/{id} — remove a saved filter.
 *
 * @param {number|string} id
 * @returns {Promise<*>}
 */
export function deleteUserCalendarFilter(id) {
	return BaseApi.delete(`/user/filter/${id}`);
}
